import React, { useEffect, useState } from 'react';
import { getAdminCategories, createCategory, updateCategory, deleteCategory } from '../../services/api';
import { Plus, Pencil, Trash2, Eye, EyeOff, Loader2, X } from 'lucide-react';
import ImageUploader from './ImageUploader';

const typeLabels = { route: 'Rotalar', collection: 'Koleksiyonlar' };

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [modal, setModal] = useState(null); // null | { mode: 'create' | 'edit', data: {} }
  const [saving, setSaving] = useState(false);
  const [modalImage, setModalImage] = useState('');

  const openModal = (cfg) => {
    setModalImage(cfg.data?.image || '');
    setModal(cfg);
  };

  const fetch = async () => {
    try {
      const res = await getAdminCategories();
      setCategories(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const filtered = filter === 'all' ? categories : categories.filter(c => c.type === filter);

  const toggleActive = async (cat) => {
    await updateCategory(cat.id, { active: !cat.active });
    fetch();
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`"${cat.name}" silinecek. Emin misiniz?`)) return;
    try {
      await deleteCategory(cat.id);
      fetch();
    } catch (err) {
      alert(err.response?.data?.detail || 'Silme işlemi başarısız');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData(e.target);
      const data = Object.fromEntries(formData.entries());
      data.image = modalImage;
      data.order = Number(data.order) || 0;
      data.count = Number(data.count) || 0;
      data.active = true;
      if (modal.mode === 'create') {
        await createCategory(data);
      } else {
        await updateCategory(modal.data.id, data);
      }
      setModal(null);
      fetch();
    } catch (err) { alert(err.response?.data?.detail || 'Hata'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#3D4A26]" size={32} /></div>;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          {['all', 'route', 'collection'].map(t => (
            <button key={t} onClick={() => setFilter(t)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === t ? 'bg-[#3D4A26] text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {t === 'all' ? 'Tümü' : typeLabels[t]}
            </button>
          ))}
        </div>
        <button onClick={() => openModal({ mode: 'create', data: { type: 'route' } })}
          className="flex items-center gap-2 bg-[#C8D843] text-[#1B1B1B] font-semibold px-4 py-2 rounded-lg hover:bg-[#b8c83a] transition-colors text-sm"
        >
          <Plus size={16} /> Yeni Kategori
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Görsel</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Ad</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Tip</th>
              <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Durum</th>
              <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Sıra</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(cat => (
              <tr key={cat.id} className={`hover:bg-gray-50 transition-colors ${!cat.active ? 'opacity-50' : ''}`}>
                <td className="px-5 py-3">
                  {cat.image ? <img src={cat.image} alt="" className="w-12 h-12 rounded-lg object-cover" /> : <div className="w-12 h-12 bg-gray-100 rounded-lg" />}
                </td>
                <td className="px-5 py-3 font-semibold text-sm text-[#1B1B1B]">{cat.name}</td>
                <td className="px-5 py-3 text-sm text-gray-600">{typeLabels[cat.type] || cat.type}</td>
                <td className="px-5 py-3 text-center">
                  <button onClick={() => toggleActive(cat)} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cat.active ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                    {cat.active ? 'Aktif' : 'Gizli'}
                  </button>
                </td>
                <td className="px-5 py-3 text-center text-sm text-gray-500">{cat.order}</td>
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => toggleActive(cat)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" title={cat.active ? 'Gizle' : 'Aktif Et'}>
                      {cat.active ? <EyeOff size={16} className="text-gray-400" /> : <Eye size={16} className="text-green-500" />}
                    </button>
                    <button onClick={() => openModal({ mode: 'edit', data: cat })} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" title="Düzenle">
                      <Pencil size={16} className="text-gray-400" />
                    </button>
                    <button onClick={() => handleDelete(cat)} className="p-1.5 hover:bg-red-50 rounded-lg transition-colors" title="Sil">
                      <Trash2 size={16} className="text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-gray-500">Kategori bulunamadı</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setModal(null)}>
          <form onSubmit={handleSave} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-lg text-[#1B1B1B]">{modal.mode === 'create' ? 'Yeni Kategori' : 'Kategori Düzenle'}</h3>
              <button type="button" onClick={() => setModal(null)} className="p-1 hover:bg-gray-100 rounded-full"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold mb-1">Ad *</label>
                <input name="name" defaultValue={modal.data?.name || ''} required className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Tip *</label>
                <select name="type" defaultValue={modal.data?.type || 'route'} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843] bg-white">
                  <option value="route">Rotalar</option>
                  <option value="collection">Koleksiyonlar</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-2">Kapak Görseli</label>
                {modalImage && (
                  <div className="relative mb-2 inline-block">
                    <img src={modalImage} alt="" className="w-full max-w-[240px] aspect-[4/3] object-cover rounded-lg border border-gray-200" />
                    <button
                      type="button"
                      onClick={() => setModalImage('')}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      title="Kaldır"
                    ><X size={12} /></button>
                  </div>
                )}
                <ImageUploader
                  category="categories"
                  label="Kapak Görseli Yükle"
                  onUpload={(url) => setModalImage(url)}
                />
                <input
                  value={modalImage}
                  onChange={(e) => setModalImage(e.target.value)}
                  className="w-full mt-2 border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#C8D843]"
                  placeholder="veya URL yapıştır"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold mb-1">Sıra</label>
                  <input name="order" type="number" defaultValue={modal.data?.order || 0} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">Tur Sayısı</label>
                  <input name="count" type="number" defaultValue={modal.data?.count || 0} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
                </div>
              </div>
            </div>
            <button type="submit" disabled={saving} className="w-full mt-5 bg-[#3D4A26] text-white font-bold py-2.5 rounded-lg hover:bg-[#2d3a1a] transition-colors disabled:opacity-50">
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
